const BASE = process.env.REACT_APP_BACKEND_URL;

// ── PDF Downloads ──────────────────────────────────────────────────────────────
async function _downloadPdf(token, endpoint, filename) {
  const res = await fetch(`${BASE}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'PDF generation failed'); }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

export const downloadCutSheetPdf = (token, planId, mode = 'branded') =>
  _downloadPdf(token, `/api/admin/production/plans/${planId}/pdf?mode=${mode}`, `WM_CutSheet_${planId}.pdf`);

export const downloadDocumentPacketPdf = (token, orderId, orderNumber, mode = 'branded') =>
  _downloadPdf(token, `/api/admin/orders/${orderId}/packet/pdf?mode=${mode}`, `WM_DocumentPacket_${orderNumber || orderId}.pdf`);

export const downloadPackingSlipPdf = (token, orderId, orderNumber, mode = 'plain') =>
  _downloadPdf(token, `/api/admin/orders/${orderId}/packing-slip/pdf?mode=${mode}`, `WM_PackingSlip_${orderNumber || orderId}.pdf`);

export const downloadShipmentReleasePdf = (token, shipmentId, mode = 'plain') =>
  _downloadPdf(token, `/api/admin/shipments/${shipmentId}/release/pdf?mode=${mode}`, `WM_ShipmentRelease_${shipmentId}.pdf`);

export const downloadCompletePacketPdf = (token, orderId, orderNumber, mode = 'plain') =>
  _downloadPdf(token, `/api/admin/orders/${orderId}/complete-packet/pdf?mode=${mode}`, `WM_CompletePacket_${orderNumber || orderId}.pdf`);



async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

// ── Public API ────────────────────────────────────────────────────────────────
export const fetchProducts = (category) =>
  request(`/api/products${category ? `?category=${category}` : ''}`);

export const fetchProductBySlug = (slug) =>
  request(`/api/products/${slug}`);

export const fetchHomepageSettings = () =>
  request('/api/homepage-settings');

export const fetchActiveCampaigns = () =>
  request('/api/campaigns/active');

// ── Admin Auth ────────────────────────────────────────────────────────────────
export const adminLogin = (email, password) =>
  request('/api/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const adminGetMe = (token) =>
  request('/api/admin/auth/me', { headers: authHeaders(token) });

// ── Admin Dashboard ───────────────────────────────────────────────────────────
export const adminGetDashboard = (token) =>
  request('/api/admin/dashboard', { headers: authHeaders(token) });

// ── Admin Products ────────────────────────────────────────────────────────────
export const adminGetProducts = (token) =>
  request('/api/admin/products', { headers: authHeaders(token) });

export const adminGetProduct = (token, id) =>
  request(`/api/admin/products/${id}`, { headers: authHeaders(token) });

export const adminCreateProduct = (token, data) =>
  request('/api/admin/products', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminUpdateProduct = (token, id, data) =>
  request(`/api/admin/products/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminDeleteProduct = (token, id) =>
  request(`/api/admin/products/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Admin Media ───────────────────────────────────────────────────────────────
export const adminGetMedia = (token) =>
  request('/api/admin/media', { headers: authHeaders(token) });

export const adminUploadMedia = async (token, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/api/admin/media/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json();
};

export const adminDeleteMedia = (token, id) =>
  request(`/api/admin/media/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Admin Homepage ────────────────────────────────────────────────────────────
export const adminGetHomepage = (token) =>
  request('/api/admin/homepage', { headers: authHeaders(token) });

export const adminUpdateHomepage = (token, data) =>
  request('/api/admin/homepage', {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

// ── Admin Campaigns ───────────────────────────────────────────────────────────
export const adminGetCampaigns = (token) =>
  request('/api/admin/campaigns', { headers: authHeaders(token) });

export const adminCreateCampaign = (token, data) =>
  request('/api/admin/campaigns', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminUpdateCampaign = (token, id, data) =>
  request(`/api/admin/campaigns/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminDeleteCampaign = (token, id) =>
  request(`/api/admin/campaigns/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Dealer Registration ───────────────────────────────────────────────────────
export const dealerRegister = (data) =>
  request('/api/dealer/register', { method: 'POST', body: JSON.stringify(data) });

export const dealerUpdateRegistration = (dealer_id, data) =>
  request(`/api/dealer/register/${dealer_id}`, { method: 'PUT', body: JSON.stringify(data) });

export const dealerSubmitApplication = (dealer_id) =>
  request(`/api/dealer/register/${dealer_id}/submit`, { method: 'POST' });

export const dealerUploadDocument = async (dealer_id, file, document_type = 'ffl') => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/api/dealer/register/${dealer_id}/documents?document_type=${document_type}`, {
    method: 'POST', body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json();
};

// ── Dealer Auth ───────────────────────────────────────────────────────────────
export const dealerLogin = (email, password) =>
  request('/api/dealer/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });

export const dealerGetMe = (token) =>
  request('/api/dealer/auth/me', { headers: authHeaders(token) });

// ── Dealer Portal ─────────────────────────────────────────────────────────────
export const dealerGetProducts = (token) =>
  request('/api/dealer/products', { headers: authHeaders(token) });

export const dealerGetProfile = (token) =>
  request('/api/dealer/profile', { headers: authHeaders(token) });

export const dealerUpdateProfile = (token, data) =>
  request('/api/dealer/profile', { method: 'PUT', headers: authHeaders(token), body: JSON.stringify(data) });

export const dealerGetOrders = (token) =>
  request('/api/dealer/orders', { headers: authHeaders(token) });

export const dealerCreateOrder = (token, data) =>
  request('/api/dealer/orders', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });

export const dealerGetOrder = (token, id) =>
  request(`/api/dealer/orders/${id}`, { headers: authHeaders(token) });

// ── Admin Dealer Management ───────────────────────────────────────────────────
export const adminGetDealers = (token, status) =>
  request(`/api/admin/dealers${status ? `?status=${status}` : ''}`, { headers: authHeaders(token) });

export const adminGetDealerStats = (token) =>
  request('/api/admin/dealers/stats', { headers: authHeaders(token) });

export const adminGetDealer = (token, id) =>
  request(`/api/admin/dealers/${id}`, { headers: authHeaders(token) });

export const adminUpdateDealerStatus = (token, id, status, note) =>
  request(`/api/admin/dealers/${id}/status`, {
    method: 'PUT', headers: authHeaders(token),
    body: JSON.stringify({ status, note }),
  });

export const adminGetDealerOrders = (token) =>
  request('/api/admin/dealer-orders', { headers: authHeaders(token) });

// ── Serialization ─────────────────────────────────────────────────────────────
export const adminGetSerialStats = (token) =>
  request('/api/admin/serials/stats', { headers: authHeaders(token) });

export const adminGenerateSerials = (token, data) =>
  request('/api/admin/serials/generate', {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(data),
  });

export const adminAssignSerial = (token, data) =>
  request('/api/admin/serials/assign', {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(data),
  });

export const adminBulkGenerateSerials = (token, orderId) =>
  request(`/api/admin/orders/${orderId}/serials/bulk-generate`, {
    method: 'POST', headers: authHeaders(token),
  });

export const adminGetOrderSerials = (token, orderId) =>
  request(`/api/admin/orders/${orderId}/serials`, { headers: authHeaders(token) });

export const adminListSerials = (token, status, category) => {
  const p = new URLSearchParams();
  if (status) p.append('status', status);
  if (category) p.append('category', category);
  const qs = p.toString();
  return request(`/api/admin/serials${qs ? '?' + qs : ''}`, { headers: authHeaders(token) });
};

export const adminTraceSerial = (token, serialNumber) =>
  request(`/api/admin/serials/${encodeURIComponent(serialNumber)}/trace`, { headers: authHeaders(token) });

export const adminMarkSerial = (token, serialNumber) =>
  request(`/api/admin/serials/${encodeURIComponent(serialNumber)}/mark`, {
    method: 'PUT', headers: authHeaders(token),
  });

// ── Shipping ──────────────────────────────────────────────────────────────────
export const adminGetShippingQueue = (token) =>
  request('/api/admin/shipping/queue', { headers: authHeaders(token) });

export const adminCreateShipment = (token, orderId, carrier = 'UPS', notes = '') =>
  request('/api/admin/shipments', {
    method: 'POST', headers: authHeaders(token),
    body: JSON.stringify({ order_id: orderId, carrier, notes }),
  });

export const adminListShipments = (token, status) =>
  request(`/api/admin/shipments${status ? '?status=' + status : ''}`, { headers: authHeaders(token) });

export const adminGetShipment = (token, shipmentId) =>
  request(`/api/admin/shipments/${shipmentId}`, { headers: authHeaders(token) });

export const adminMarkShipped = (token, shipmentId, tracking, carrier, notes) =>
  request(`/api/admin/shipments/${shipmentId}/ship`, {
    method: 'POST', headers: authHeaders(token),
    body: JSON.stringify({ tracking_number: tracking, carrier, notes }),
  });

// ── Document Packets ──────────────────────────────────────────────────────────
export const adminGeneratePacket = (token, orderId) =>
  request(`/api/admin/orders/${orderId}/packet/generate`, { method: 'POST', headers: authHeaders(token) });

export const adminGetPacket = (token, orderId) =>
  request(`/api/admin/orders/${orderId}/packet`, { headers: authHeaders(token) });

export const adminUpdateChecklistItem = (token, orderId, itemId, status, notes) =>
  request(`/api/admin/orders/${orderId}/packet/checklist/${itemId}`, {
    method: 'PUT', headers: authHeaders(token),
    body: JSON.stringify({ status, notes }),
  });

export const adminUpdatePhysicalFiling = (token, orderId, data) =>
  request(`/api/admin/orders/${orderId}/packet/physical`, {
    method: 'PUT', headers: authHeaders(token),
    body: JSON.stringify(data),
  });

// ── Demo System ───────────────────────────────────────────────────────────────
export const adminDemoStatus = (token) =>
  request('/api/admin/demo/status', { headers: authHeaders(token) });

export const adminDemoSeed = (token) =>
  request('/api/admin/demo/seed', { method: 'POST', headers: authHeaders(token) });

export const adminDemoReset = (token) =>
  request('/api/admin/demo/reset', { method: 'POST', headers: authHeaders(token) });
export const adminGetAuditLog = (token, objectType, limit = 100) => {
  const p = new URLSearchParams();
  if (objectType) p.append('object_type', objectType);
  if (limit) p.append('limit', limit);
  return request(`/api/admin/audit?${p.toString()}`, { headers: authHeaders(token) });
};

export const adminGetObjectAudit = (token, objectType, objectId) =>
  request(`/api/admin/audit/${objectType}/${objectId}`, { headers: authHeaders(token) });
export const adminGetOrderPipeline = (token) =>
  request('/api/admin/orders/pipeline', { headers: authHeaders(token) });

export const adminGetOrders = (token, status, orderType) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (orderType) params.append('order_type', orderType);
  const qs = params.toString();
  return request(`/api/admin/orders${qs ? '?' + qs : ''}`, { headers: authHeaders(token) });
};

export const adminGetOrder = (token, id) =>
  request(`/api/admin/orders/${id}`, { headers: authHeaders(token) });

export const adminUpdateOrderStatus = (token, id, status, note) =>
  request(`/api/admin/orders/${id}/status`, {
    method: 'PUT', headers: authHeaders(token),
    body: JSON.stringify({ status, note }),
  });

export const adminValidateOrder = (token, id) =>
  request(`/api/admin/orders/${id}/validate`, { method: 'POST', headers: authHeaders(token) });

export const adminMockPayment = (token, id, success) =>
  request(`/api/admin/orders/${id}/payment`, {
    method: 'POST', headers: authHeaders(token),
    body: JSON.stringify({ success }),
  });

export const adminAddOrderNote = (token, id, note) =>
  request(`/api/admin/orders/${id}/note`, {
    method: 'POST', headers: authHeaders(token),
    body: JSON.stringify({ note }),
  });

// ── Payment Service ───────────────────────────────────────────────────────────
export const adminGetOrderPayments = (token, orderId) =>
  request(`/api/admin/orders/${orderId}/payments`, { headers: authHeaders(token) });

export const adminRefundPayment = (token, paymentId, amount) =>
  request(`/api/admin/payments/${paymentId}/refund`, {
    method: 'POST', headers: authHeaders(token),
    body: JSON.stringify({ amount: amount || null }),
  });

export const adminGetAllPayments = (token) =>
  request('/api/admin/payments', { headers: authHeaders(token) });

export const adminGetProviders = (token) =>
  request('/api/admin/providers', { headers: authHeaders(token) });

// ── Production Engine ─────────────────────────────────────────────────────────
export const adminGetProductionQueue = (token) =>
  request('/api/admin/production/queue', { headers: authHeaders(token) });

export const adminGenerateProductionPlan = (token) =>
  request('/api/admin/production/generate', { method: 'POST', headers: authHeaders(token) });

export const adminGetProductionPlans = (token) =>
  request('/api/admin/production/plans', { headers: authHeaders(token) });

export const adminGetProductionPlan = (token, planId) =>
  request(`/api/admin/production/plans/${planId}`, { headers: authHeaders(token) });
